"use client";

import {
	detectOurBrandInAiOverview,
	getCompetitorNames,
	isCompetitorBrand,
	isOurBrand,
} from "@repo/core/client";
import {
	processDiscoveryResponse,
	renderMarkdownSync,
} from "@shared/lib/report-utils";
import {
	AlertCircleIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	ExternalLinkIcon,
	ListIcon,
	SearchIcon,
	SparklesIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface GoogleAiOverviewData {
	check_url: string;
	aiOverview?: {
		summary: string;
		/** Full markdown content for rendering and brand highlighting */
		markdown?: string;
		sections: Array<{
			title?: string;
			content: string;
		}>;
		/** Reference-based brand mentions (cited domains/sources) */
		brandMentions: Array<{
			name: string;
			description: string;
			domain: string;
			url: string;
		}>;
		/** Text-based brand mentions extracted from markdown (bold text, headers) */
		textBrandMentions?: Array<{
			name: string;
			context: string;
			position: number;
		}>;
	};
	organicResults: Array<{
		rank: number;
		title: string;
		domain: string;
		websiteName: string;
		url: string;
		description: string;
	}>;
}

interface GoogleAiOverviewSectionProps {
	data: GoogleAiOverviewData;
	brandSnapshot?: any;
	propertyCompetitors?: any[];
	/** Property aliases for brand detection (e.g., ["Galleri", "Pathfinder"] for a property named "Grail") */
	propertyAliases?: string[];
	/**
	 * Whether this subject has a focal brand of its own. False for CATEGORY — the
	 * property IS the market. Everything on this panel that says "brand" means the
	 * focal one (in the AI Overview, at organic rank N, ahead of N competitors), so
	 * with no focal subject those readings are dropped rather than computed against
	 * the category's own name. Tracked brands still render; they are just not
	 * labelled as rivals of anybody.
	 */
	hasFocalSubject?: boolean;
}

type TabType = "ai-overview" | "organic-results";

export function GoogleAiOverviewSection({
	data,
	brandSnapshot,
	propertyCompetitors,
	propertyAliases = [],
	hasFocalSubject = true,
}: GoogleAiOverviewSectionProps) {
	// Identity for focal-brand matching only. Empty when there is no focal
	// subject, so nothing in the answer text is highlighted as "our brand" and no
	// stored our-brand flag is believed.
	const focalBrandSnapshot = hasFocalSubject ? brandSnapshot : undefined;
	const focalPropertyAliases = hasFocalSubject ? propertyAliases : [];
	const [showMore, setShowMore] = useState(false);
	const [processedSummary, setProcessedSummary] = useState<any>(null);
	const [processedSections, setProcessedSections] = useState<
		Map<number, any>
	>(new Map());
	// Default to organic results if no AI Overview, otherwise show AI Overview first
	const [activeTab, setActiveTab] = useState<TabType>(() =>
		data.aiOverview ? "ai-overview" : "organic-results",
	);

	const competitorNames = useMemo(
		() => getCompetitorNames(brandSnapshot, propertyCompetitors),
		[brandSnapshot, propertyCompetitors],
	);

	// Check whether our brand is in the AI Overview via the shared three-tier
	// detector used by the report and nightly storage paths. Routing the badge
	// through the same helper means the badge can never disagree with the
	// tab dot / per-query mention count / CompetitorSOV again — they all
	// answer "is the brand in this AI Overview?" the same way now.
	const isBrandInAiOverview = useMemo(() => {
		if (!hasFocalSubject) return false;
		return detectOurBrandInAiOverview(data.aiOverview, {
			brandName: brandSnapshot?.data?.name,
			// Display historically didn't match by domain. Keep it that way so we
			// don't shift behavior on the badge — only the storage side passes the
			// brand domain.
			aliases: propertyAliases,
		}).found;
	}, [data.aiOverview, brandSnapshot, propertyAliases, hasFocalSubject]);

	// Calculate brand presence in organic results
	const brandOrganicRank = useMemo(() => {
		if (!hasFocalSubject) return null;
		return (
			data.organicResults?.find((result) => {
				const resultIsOur =
					isOurBrand(
						result.websiteName,
						brandSnapshot,
						propertyAliases,
					) ||
					isOurBrand(result.title, brandSnapshot, propertyAliases) ||
					(brandSnapshot?.domain &&
						result.domain.includes(
							brandSnapshot.domain
								.replace(/^https?:\/\//, "")
								.replace(/^www\./, ""),
						));
				return resultIsOur;
			})?.rank || null
		);
	}, [data.organicResults, brandSnapshot, propertyAliases, hasFocalSubject]);

	const competitorOrganicRanks = useMemo(() => {
		return (
			data.organicResults
				?.filter((result) => {
					return (
						isCompetitorBrand(
							result.websiteName,
							competitorNames,
						) ||
						propertyCompetitors?.some(
							(competitor) =>
								competitor.url &&
								result.domain.includes(
									competitor.url
										.replace(/^https?:\/\//, "")
										.replace(/^www\./, ""),
								),
						)
					);
				})
				.map((r) => r.rank) || []
		);
	}, [data.organicResults, competitorNames, propertyCompetitors]);

	// Process AI Overview summary with proper markdown rendering
	useEffect(() => {
		const processSummary = async () => {
			if (!data.aiOverview?.summary) return;

			try {
				const processed = await processDiscoveryResponse(
					{
						answer_summary: data.aiOverview.summary,
						brands_mentioned: data.aiOverview.brandMentions || [],
						citations: [],
					},
					{
						competitorNames,
						brandSnapshot: focalBrandSnapshot,
						propertyAliases: focalPropertyAliases, // Pass aliases for brand highlighting
					},
				);
				setProcessedSummary(processed);
			} catch (error) {
				console.error(
					"Failed to process Google AI Overview summary:",
					error,
				);
				// Fallback to sync rendering
				const highlighted = highlightBrandInTextFallback(
					data.aiOverview.summary,
				);
				setProcessedSummary({
					htmlContent: highlighted,
					isMarkdown: false,
					brandMentions: [],
					citations: [],
				});
			}
		};

		processSummary();
	}, [data.aiOverview, competitorNames, brandSnapshot, propertyAliases]);

	// Process sections (if shown)
	useEffect(() => {
		if (!showMore || !data.aiOverview?.sections) return;

		const processSections = async () => {
			const newProcessed = new Map();

			for (let i = 0; i < data.aiOverview!.sections.length; i++) {
				const section = data.aiOverview!.sections[i];

				try {
					const processed = await processDiscoveryResponse(
						{
							answer_summary: section.content,
							brands_mentioned: [],
							citations: [],
						},
						{
							competitorNames,
							brandSnapshot: focalBrandSnapshot,
							propertyAliases: focalPropertyAliases, // Pass aliases for brand highlighting
						},
					);
					newProcessed.set(i, processed);
				} catch (error) {
					console.error(`Failed to process section ${i}:`, error);
					// Fallback
					newProcessed.set(i, {
						htmlContent: renderMarkdownSync(section.content),
						isMarkdown: false,
						brandMentions: [],
						citations: [],
					});
				}
			}

			setProcessedSections(newProcessed);
		};

		processSections();
	}, [
		showMore,
		data.aiOverview,
		competitorNames,
		brandSnapshot,
		propertyAliases,
	]);

	// Fallback highlighting function (used if async processing fails)
	const highlightBrandInTextFallback = (text: string): string => {
		let highlighted = text;

		// Collect all our brand names (primary + aliases)
		const ourBrandNames: string[] = [];
		if (hasFocalSubject && brandSnapshot?.data?.name) {
			ourBrandNames.push(brandSnapshot.data.name);
		}
		if (hasFocalSubject && propertyAliases && propertyAliases.length > 0) {
			ourBrandNames.push(...propertyAliases.filter(Boolean));
		}

		// Sort by length (longest first) to avoid partial replacement issues
		const sortedBrandNames = [...ourBrandNames].sort(
			(a, b) => b.length - a.length,
		);

		// Highlight our brand names (GREEN)
		sortedBrandNames.forEach((brandName) => {
			if (!brandName) return;
			const escapedBrand = brandName.replace(
				/[.*+?^${}()|[\]\\]/g,
				"\\$&",
			);
			const regex = new RegExp(`\\b(${escapedBrand})\\b`, "gi");
			highlighted = highlighted.replace(
				regex,
				`<mark class="bg-green-100 text-green-900 px-1 rounded font-semibold">$1</mark>`,
			);
		});

		// Highlight competitors (ORANGE)
		competitorNames.forEach((competitor) => {
			const escapedCompetitor = competitor.replace(
				/[.*+?^${}()|[\]\\]/g,
				"\\$&",
			);
			const regex = new RegExp(`\\b(${escapedCompetitor})\\b`, "gi");
			highlighted = highlighted.replace(
				regex,
				`<mark class="bg-orange-100 text-orange-900 px-1 rounded font-medium">$1</mark>`,
			);
		});

		return highlighted;
	};

	const hasAiOverview = !!data.aiOverview;
	const hasOrganicResults = data.organicResults?.length > 0;

	if (!hasAiOverview && !hasOrganicResults) {
		return (
			<div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
				<div className="text-gray-600 text-sm">
					No AI Overview or organic results available for this query.
				</div>
			</div>
		);
	}

	return (
		<div className="bg-gray-50 rounded-lg border border-gray-200">
			{/* Header with Quick Stats */}
			<div className="p-4 border-b border-gray-200">
				<div className="flex items-center justify-between mb-3">
					<h5 className="font-medium text-gray-900">
						Google AI Overview
					</h5>
					{data.check_url && (
						<a
							href={data.check_url}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
						>
							<span>View this query on Google</span>
							<ExternalLinkIcon className="w-3 h-3" />
						</a>
					)}
				</div>

				{/* Quick Stats Row */}
				<div className="flex flex-wrap gap-2 mb-3">
					{/* AI Overview Status */}
					{hasAiOverview ? (
						<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
							<SparklesIcon className="w-3 h-3 mr-1" />
							AI Overview Active
						</span>
					) : (
						<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
							<AlertCircleIcon className="w-3 h-3 mr-1" />
							No AI Overview
						</span>
					)}

					{/* Brand in AI Overview — a focal-brand reading, dropped when the
              subject is the market itself. */}
					{hasAiOverview &&
						hasFocalSubject &&
						(isBrandInAiOverview ? (
							<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
								✓ Brand in AI Overview
							</span>
						) : (
							<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
								Brand not in AI Overview
							</span>
						))}

					{/* Brand Organic Rank — likewise relative to a focal brand. */}
					{!hasFocalSubject ? null : brandOrganicRank ? (
						<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
							<SearchIcon className="w-3 h-3 mr-1" />
							Organic #{brandOrganicRank}
						</span>
					) : (
						hasOrganicResults && (
							<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
								<SearchIcon className="w-3 h-3 mr-1" />
								Not in top {data.organicResults?.length || 10}
							</span>
						)
					)}

					{/* Competitor Rankings — "competitors" only means something next to a
              focal brand; in a category these are simply the tracked brands. */}
					{hasFocalSubject && competitorOrganicRanks.length > 0 && (
						<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
							{competitorOrganicRanks.length} competitor
							{competitorOrganicRanks.length !== 1 ? "s" : ""} in
							top {data.organicResults?.length || 10}
						</span>
					)}
				</div>
			</div>

			{/* Tab Navigation - Always show both tabs */}
			<div className="flex border-b border-gray-200">
				<button
					type="button"
					onClick={() => setActiveTab("ai-overview")}
					className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
						activeTab === "ai-overview"
							? "border-b-2 border-blue-600 text-blue-600 bg-blue-50"
							: "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
					}`}
				>
					<SparklesIcon className="w-4 h-4" />
					AI Overview
					{data.aiOverview?.brandMentions &&
						data.aiOverview.brandMentions.length > 0 && (
							<span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
								{data.aiOverview.brandMentions.length}
							</span>
						)}
				</button>
				<button
					type="button"
					onClick={() => setActiveTab("organic-results")}
					className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
						activeTab === "organic-results"
							? "border-b-2 border-blue-600 text-blue-600 bg-blue-50"
							: "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
					}`}
				>
					<ListIcon className="w-4 h-4" />
					Organic Results
					{hasOrganicResults && (
						<span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
							{data.organicResults?.length || 0}
						</span>
					)}
				</button>
			</div>

			<div className="p-4">
				{/* AI Overview Content */}
				{activeTab === "ai-overview" &&
					(hasAiOverview ? (
						<div className="space-y-4">
							{/* Note about AI Overview variability */}
							<div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
								<strong>Note:</strong> AI Overview results may
								vary based on your location, search history, and
								other factors. For closest match to this
								analysis, open the link in Incognito mode.
							</div>

							{/* Summary */}
							<div>
								{processedSummary ? (
									<div
										// biome-ignore lint/security/noDangerouslySetInnerHtml: LLM answer through the report markdown renderer; the sanitizing renderer strips the highlight spans this view depends on, moving to a sanitized pipeline that preserves them is tracked follow-up work
										dangerouslySetInnerHTML={{
											__html: processedSummary.htmlContent,
										}}
										className="text-sm text-gray-700 bg-white p-3 rounded border
                             prose prose-sm max-w-none
                             prose-headings:font-bold prose-headings:text-gray-900
                             prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3
                             prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2
                             prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-3
                             prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                             prose-ul:list-disc prose-ul:ml-6 prose-ul:mb-4
                             prose-ol:list-decimal prose-ol:ml-6 prose-ol:mb-4
                             prose-li:mb-1
                             prose-table:border-collapse prose-table:w-full prose-table:my-4
                             prose-th:border prose-th:border-gray-300 prose-th:bg-gray-50 
                             prose-th:px-3 prose-th:py-2 prose-th:font-semibold prose-th:text-left
                             prose-td:border prose-td:border-gray-300 prose-td:px-3 prose-td:py-2
                             prose-strong:text-gray-900 prose-strong:font-semibold"
									/>
								) : (
									<div className="bg-white p-3 rounded border">
										<div className="flex items-center gap-2 text-gray-500 text-sm">
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
											<span>Processing markdown...</span>
										</div>
									</div>
								)}
							</div>

							{/* Show More Button */}
							{data.aiOverview!.sections.length > 0 && (
								<div>
									<button
										type="button"
										onClick={() => setShowMore(!showMore)}
										className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
									>
										<span>
											{showMore
												? "Show Less"
												: "Show More"}
										</span>
										{showMore ? (
											<ChevronUpIcon className="w-4 h-4" />
										) : (
											<ChevronDownIcon className="w-4 h-4" />
										)}
									</button>
								</div>
							)}

							{/* Expanded Sections */}
							{showMore &&
								data.aiOverview!.sections.length > 0 && (
									<div className="space-y-3 border-t pt-4">
										{data.aiOverview!.sections.map(
											(section, index) => (
												<div key={index}>
													{section.title && (
														<h6 className="font-medium text-gray-900 mb-2">
															{section.title}
														</h6>
													)}
													{processedSections.get(
														index,
													) ? (
														<div
															// biome-ignore lint/security/noDangerouslySetInnerHtml: LLM answer through the report markdown renderer; the sanitizing renderer strips the highlight spans this view depends on, moving to a sanitized pipeline that preserves them is tracked follow-up work
															dangerouslySetInnerHTML={{
																__html: processedSections.get(
																	index,
																).htmlContent,
															}}
															className="prose prose-sm max-w-none
                                   text-sm text-gray-700 bg-white p-3 rounded border
                                   prose-headings:font-bold prose-headings:text-gray-900
                                   prose-p:text-gray-700 prose-p:mb-2
                                   prose-a:text-blue-600 hover:prose-a:underline
                                   prose-ul:list-disc prose-ul:ml-6
                                   prose-ol:list-decimal prose-ol:ml-6
                                   prose-li:mb-1
                                   prose-table:border-collapse prose-th:border prose-td:border
                                   prose-strong:font-semibold"
														/>
													) : (
														<div className="bg-white p-3 rounded border">
															<div className="flex items-center gap-2 text-gray-500 text-xs">
																<div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400" />
																<span>
																	Processing...
																</span>
															</div>
														</div>
													)}
												</div>
											),
										)}
									</div>
								)}

							{/* Brand Mentions */}
							{data.aiOverview!.brandMentions.length > 0 && (
								<div className="border-t pt-4">
									<h6 className="font-medium text-gray-900 mb-2">
										{hasFocalSubject
											? "Brand Mentions"
											: "Brands named"}{" "}
										({data.aiOverview!.brandMentions.length}
										)
									</h6>
									<div className="space-y-2">
										{data.aiOverview!.brandMentions.map(
											(mention, index) => {
												const mentionIsOur =
													hasFocalSubject &&
													isOurBrand(
														mention.name,
														brandSnapshot,
														propertyAliases,
													);
												const mentionIsCompetitor =
													isCompetitorBrand(
														mention.name,
														competitorNames,
													);

												return (
													<div
														key={index}
														className={`flex items-center justify-between text-sm p-2 rounded border transition-colors ${
															mentionIsOur
																? "bg-green-50 border-green-200"
																: mentionIsCompetitor
																	? "bg-orange-50 border-orange-200"
																	: "bg-white border-gray-200"
														}`}
													>
														<div className="flex-1 min-w-0">
															<div className="font-medium text-gray-900 truncate">
																{mention.name}
															</div>
														</div>
														<div className="flex items-center space-x-2 ml-2">
															{mentionIsOur && (
																<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
																	Our Brand
																</span>
															)}
															{mentionIsCompetitor &&
																hasFocalSubject && (
																	<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
																		Competitor
																	</span>
																)}
															<a
																href={
																	mention.url
																}
																target="_blank"
																rel="noopener noreferrer"
																className="text-blue-600 hover:text-blue-800"
															>
																<ExternalLinkIcon className="w-4 h-4" />
															</a>
														</div>
													</div>
												);
											},
										)}
									</div>
								</div>
							)}
						</div>
					) : (
						<div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
							<AlertCircleIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
							<h6 className="font-medium text-gray-700 mb-2">
								No AI Overview for this query
							</h6>
							<p className="text-sm text-gray-500">
								Google did not generate an AI Overview for this
								search query at the time of analysis. AI
								Overviews appear for some queries but not
								others, and their availability can change over
								time.
							</p>
						</div>
					))}

				{/* Organic Search Results */}
				{activeTab === "organic-results" && (
					<div>
						{hasOrganicResults ? (
							<div className="space-y-2">
								{data.organicResults!.map((result) => {
									// Use the same brand detection logic as other parts of the app
									// Include propertyAliases for alias-based detection and check title as well
									const resultIsOur =
										hasFocalSubject &&
										(isOurBrand(
											result.websiteName,
											brandSnapshot,
											propertyAliases,
										) ||
											isOurBrand(
												result.title,
												brandSnapshot,
												propertyAliases,
											) ||
											(brandSnapshot?.domain &&
												result.domain.includes(
													brandSnapshot.domain
														.replace(
															/^https?:\/\//,
															"",
														)
														.replace(/^www\./, ""),
												)));

									// Check both website name and domain for competitor detection
									const resultIsCompetitor =
										isCompetitorBrand(
											result.websiteName,
											competitorNames,
										) ||
										propertyCompetitors?.some(
											(competitor) =>
												competitor.url &&
												result.domain.includes(
													competitor.url
														.replace(
															/^https?:\/\//,
															"",
														)
														.replace(/^www\./, ""),
												),
										);

									return (
										<div
											key={result.rank}
											className={`text-sm p-2 rounded border transition-colors ${
												resultIsOur
													? "bg-green-50 border-green-200"
													: resultIsCompetitor
														? "bg-orange-50 border-orange-200"
														: "bg-white border-gray-200"
											}`}
										>
											<div className="flex items-center justify-between">
												<div className="flex-1 min-w-0">
													<div className="flex items-center space-x-2">
														<span
															className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
																resultIsOur
																	? "bg-green-200 text-green-800"
																	: resultIsCompetitor
																		? "bg-orange-200 text-orange-800"
																		: "bg-gray-100 text-gray-600"
															}`}
														>
															{result.rank}
														</span>
														<span className="font-medium text-gray-900 truncate">
															{result.websiteName}
														</span>
													</div>
													<div className="text-xs text-gray-500 ml-8 truncate">
														{result.domain}
													</div>
												</div>
												<div className="flex items-center space-x-2 ml-2">
													{resultIsOur && (
														<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
															Our Site
														</span>
													)}
													{resultIsCompetitor && (
														<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
															{hasFocalSubject
																? "Competitor"
																: "Tracked brand"}
														</span>
													)}
													<a
														href={result.url}
														target="_blank"
														rel="noopener noreferrer"
														className="text-blue-600 hover:text-blue-800"
													>
														<ExternalLinkIcon className="w-4 h-4" />
													</a>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<div className="text-sm text-gray-500 bg-gray-50 p-3 rounded border">
								No organic search results available for this
								query.
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
